import { useEffect, useState } from "react";

import AdminPage from "./components/AdminPage";
import AdminCard from "./components/AdminCard";
import AdminLoading from "./components/AdminLoading";

import {
  getPredictionPresets,
  getPreset,
  createPreset,
  updatePresetSettings,
  activatePreset,
  deletePreset,
  duplicatePreset,
} from "./api/predictionApi";

const SETTING_FIELDS = [
  {
    key: "min_odd",
    label: "Minimum Odd",
    min: 1,
    max: 20,
    step: 0.01,
  },
  {
    key: "max_odd",
    label: "Maximum Odd",
    min: 1,
    max: 20,
    step: 0.01,
  },
  {
    key: "min_margin",
    label: "Minimum Margin",
    min: 0,
    max: 100,
    step: 1,
  },
  {
    key: "high_value_margin",
    label: "High Value Margin",
    min: 0,
    max: 100,
    step: 1,
  },
  {
    key: "max_predictions_per_day",
    label: "Maximum Predictions / Day",
    min: 1,
    max: 10000,
    step: 1,
  },
  {
    key: "min_margin_1x2",
    label: "Minimum Margin 1X2",
    min: 1,
    max: 20,
    step: 0.01,
  },
  {
    key: "min_margin_goals",
    label: "Minimum Margin Goals",
    min: 1,
    max: 20,
    step: 0.01,
  },
  {
    key: "min_margin_btts",
    label: "Minimum Margin BTTS",
    min: 1,
    max: 20,
    step: 0.01,
  },
  {
    key: "min_odd_1x2",
    label: "Minimum Odd 1X2",
    min: 1,
    max: 20,
    step: 0.01,
  },
  {
    key: "max_odd_1x2",
    label: "Maximum Odd 1X2",
    min: 1,
    max: 20,
    step: 0.01,
  },
  {
    key: "min_odd_goals",
    label: "Minimum Odd Goals",
    min: 1,
    max: 20,
    step: 0.01,
  },
  {
    key: "max_odd_goals",
    label: "Maximum Odd Goals",
    min: 1,
    max: 20,
    step: 0.01,
  },
  {
    key: "min_odd_btts",
    label: "Minimum Odd BTTS",
    min: 1,
    max: 20,
    step: 0.01,
  },
  {
    key: "max_odd_btts",
    label: "Maximum Odd BTTS",
    min: 1,
    max: 20,
    step: 0.01,
  }
];

export default function PredictionSettings() {
  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);

  const [activating, setActivating] = useState(false);

  const [presets, setPresets] = useState([]);

  const [selectedPreset, setSelectedPreset] = useState(null);

  const [settings, setSettings] = useState({});

  const [showCreate, setShowCreate] = useState(false);

  const [newPreset, setNewPreset] = useState({
    name: "",
    description: "",
    copyFromPresetId: "",
  });

  useEffect(() => {
    loadPresets();
  }, []);

  async function loadPresets(selectId = null) {
    try {
      setLoading(true);

      const data = await getPredictionPresets();

      setPresets(data);

      let preset;

      if (selectId) {
        preset = data.find((p) => p.id === Number(selectId));
      }

      if (!preset) {
        preset = data.find((p) => p.active) || data[0];
      }

      if (preset) {
        await loadPreset(preset.id);
      }
    } catch (error) {
      console.error("Error loading presets:", error);
    } finally {
      setLoading(false);
    }
  }

  async function loadPreset(id) {
    try {
      const data = await getPreset(id);

      setSelectedPreset(data.preset);

      setSettings(data.settings);
    } catch (error) {
      console.error("Error loading preset:", error);
    }
  }

  async function handlePresetChange(e) {
    await loadPreset(Number(e.target.value));
  }

  function updateField(key, value) {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  async function saveSettings() {
    if (!selectedPreset) return;

    try {
      setSaving(true);

      await updatePresetSettings(selectedPreset.id, settings);

      await loadPresets(selectedPreset.id);

      alert("Settings saved.");
    } catch (error) {
      console.error(error);

      alert(error.response?.data?.message || "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  }

  async function handleActivate() {
    if (!selectedPreset) return;

    try {
      setActivating(true);

      await activatePreset(selectedPreset.id);

      await loadPresets(selectedPreset.id);

      alert("Preset activated.");
    } catch (error) {
      console.error(error);

      alert(error.response?.data?.message || "Activation failed.");
    } finally {
      setActivating(false);
    }
  }

  async function handleCreatePreset() {
    if (!newPreset.name.trim()) {
      alert("Preset name is required.");
      return;
    }

    try {
      const result = await createPreset({
        name: newPreset.name,
        description: newPreset.description,
        copyFromPresetId: newPreset.copyFromPresetId || null,
      });

      setShowCreate(false);

      setNewPreset({
        name: "",
        description: "",
        copyFromPresetId: "",
      });

      await loadPresets(result.id);
    } catch (error) {
      console.error(error);

      alert(error.response?.data?.message || "Unable to create preset.");
    }
  }

  async function handleDuplicatePreset() {
    if (!selectedPreset) return;

    const name = prompt(
      "Name for the duplicated preset:",
      `${selectedPreset.name} Copy`,
    );

    if (!name) return;

    try {
      const result = await duplicatePreset(
        selectedPreset.id,
        name,
        selectedPreset.description,
      );

      await loadPresets(result.id);
    } catch (error) {
      console.error(error);

      alert(error.response?.data?.message || "Unable to duplicate preset.");
    }
  }

  async function handleDeletePreset() {
    if (!selectedPreset) return;

    if (selectedPreset.active) {
      alert("You cannot delete the active preset.");
      return;
    }

    const confirmed = window.confirm(`Delete '${selectedPreset.name}'?`);

    if (!confirmed) return;

    try {
      await deletePreset(selectedPreset.id);

      await loadPresets();
    } catch (error) {
      console.error(error);

      alert(error.response?.data?.message || "Unable to delete preset.");
    }
  }

  if (loading) {
    return (
      <AdminPage
        title='Prediction Settings'
        subtitle='Manage prediction presets.'
      >
        <AdminLoading />
      </AdminPage>
    );
  }

  return (
    <AdminPage
      title='Prediction Settings'
      subtitle='Manage prediction presets and prediction engine parameters.'
    >
      <AdminCard className='p-6 max-w-5xl'>
        {/* Header */}

        <div
          className='
          flex
          flex-col
          md:flex-row
          justify-between
          gap-4
          mb-8
        '
        >
          <div></div>

          <button
            onClick={() => setShowCreate(true)}
            className='
              px-5
              py-3
              rounded-xl
              bg-blue-600
              hover:bg-blue-500
              text-white
              font-bold
            '
          >
            + New Preset
          </button>
        </div>

        {/* Preset selector */}

        <div
          className='
          flex
          flex-col
          md:flex-row
          gap-4
          mb-6
        '
        >
          <select
            value={selectedPreset?.id || ""}
            onChange={handlePresetChange}
            className='
              flex-1
              bg-gray-800
              border
              border-gray-700
              rounded-xl
              px-4
              py-3
              text-white
            '
          >
            {presets.map((preset) => (
              <option key={preset.id} value={preset.id}>
                {preset.name}
                {preset.active ? " (Active)" : ""}
              </option>
            ))}
          </select>

          <button
            onClick={handleDuplicatePreset}
            disabled={!selectedPreset}
            className='
              px-4
              py-3
              rounded-xl
              bg-purple-600
              hover:bg-purple-500
              text-white
              font-bold
              disabled:opacity-40
            '
          >
            Duplicate
          </button>

          <button
            onClick={handleDeletePreset}
            disabled={!selectedPreset || selectedPreset.active}
            className='
              px-4
              py-3
              rounded-xl
              bg-red-600
              hover:bg-red-500
              text-white
              font-bold
              disabled:opacity-40
            '
          >
            Delete
          </button>
        </div>

        {selectedPreset && (
          <div
            className='
                mb-8
                bg-black/30
                border
                border-green-500/20
                rounded-xl
                p-5
              '
          >
            <h3
              className='
                text-green-300
                font-bold
                text-lg
              '
            >
              {selectedPreset.name}
            </h3>

            <p className='text-gray-400 text-sm'>
              {selectedPreset.description || "No description"}
            </p>
          </div>
        )}

        {/* Settings */}

        <div
          className='
          grid
          grid-cols-1
          md:grid-cols-2
          gap-5
        '
        >
          {SETTING_FIELDS.map((field) => (
            <div
              key={field.key}
              className='
                  bg-black/30
                  border
                  border-green-500/20
                  rounded-xl
                  p-4
                '
            >
              <label
                className='
                  block
                  mb-2
                  text-gray-400
                  text-sm
                '
              >
                {field.label}
              </label>

              <input
                type='number'
                value={settings[field.key] ?? ""}
                min={field.min}
                max={field.max}
                step={field.step}
                onChange={(e) => updateField(field.key, e.target.value)}
                className='
                    w-full
                    bg-gray-800/60
                    border
                    border-gray-700
                    rounded-lg
                    px-4
                    py-3
                    text-white
                    outline-none
                    focus:ring-2
                    focus:ring-green-400
                  '
              />
            </div>
          ))}
        </div>

        {/* Actions */}

        <div
          className='
          flex
          justify-end
          gap-3
          mt-8
        '
        >
          <button
            onClick={handleActivate}
            disabled={activating || selectedPreset?.active}
            className='
              px-5
              py-3
              rounded-xl
              bg-purple-600
              hover:bg-purple-500
              text-white
              font-bold
              disabled:opacity-40
            '
          >
            {activating
              ? "Activating..."
              : selectedPreset?.active
                ? "Active"
                : "Activate"}
          </button>

          <button
            onClick={saveSettings}
            disabled={saving}
            className='
              px-5
              py-3
              rounded-xl
              bg-green-600
              hover:bg-green-500
              text-white
              font-bold
              disabled:opacity-50
            '
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </AdminCard>

      {/* Create Modal */}

      {showCreate && (
        <div
          className='
            fixed
            inset-0
            bg-black/70
            flex
            items-center
            justify-center
            z-50
          '
        >
          <div
            className='
              bg-gray-900
              border
              border-gray-700
              rounded-xl
              p-6
              w-full
              max-w-md
            '
          >
            <h3
              className='
                text-xl
                text-green-300
                font-bold
                mb-5
              '
            >
              Create Preset
            </h3>

            <input
              placeholder='Name'
              value={newPreset.name}
              onChange={(e) =>
                setNewPreset((prev) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
              className='
                  w-full
                  mb-3
                  bg-gray-800
                  border
                  border-gray-700
                  rounded-lg
                  px-4
                  py-3
                  text-white
                '
            />

            <input
              placeholder='Description'
              value={newPreset.description}
              onChange={(e) =>
                setNewPreset((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              className='
                  w-full
                  mb-3
                  bg-gray-800
                  border
                  border-gray-700
                  rounded-lg
                  px-4
                  py-3
                  text-white
                '
            />

            <select
              value={newPreset.copyFromPresetId}
              onChange={(e) =>
                setNewPreset((prev) => ({
                  ...prev,
                  copyFromPresetId: e.target.value,
                }))
              }
              className='
                  w-full
                  mb-5
                  bg-gray-800
                  border
                  border-gray-700
                  rounded-lg
                  px-4
                  py-3
                  text-white
                '
            >
              <option value=''>Start empty</option>

              {presets.map((preset) => (
                <option key={preset.id} value={preset.id}>
                  Copy from {preset.name}
                </option>
              ))}
            </select>

            <div
              className='
                flex
                justify-end
                gap-3
              '
            >
              <button
                onClick={() => setShowCreate(false)}
                className='
                    px-4
                    py-3
                    rounded-xl
                    bg-gray-700
                    text-white
                  '
              >
                Cancel
              </button>

              <button
                onClick={handleCreatePreset}
                className='
                    px-4
                    py-3
                    rounded-xl
                    bg-green-600
                    text-white
                    font-bold
                  '
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminPage>
  );
}
